<template>
  <v-card>
    <v-card-title>
      Subscriptions
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        class="mr-2"
        @click.stop="openSubscriptionTypeModal()"
      >
        <v-icon>{{ icons.mdiPlaylistPlus }}</v-icon>
        <span>New Type</span>
      </v-btn>
      <v-btn
        color="success"
        @click.stop="addSubscription()"
      >
        <v-icon>{{ icons.mdiPlus }}</v-icon>
        <span>New Subscription</span>
      </v-btn>
    </v-card-title>
    <v-simple-table>
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-uppercase">
              Customer
            </th>
            <th class="text-uppercase">
              Type
            </th>
            <th class="text-uppercase">
              Name
            </th>
            <th class="text-uppercase">
              Next Renewal
            </th>
            <th class="text-right text-uppercase">
              Price
            </th>
            <th class="text-center text-uppercase">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(subscription, subscriptionKey) in subscriptions"
            :key="subscriptionKey"
          >
            <td>{{ subscription.customer.company_name }}</td>
            <td>{{ subscription.subscription_type.name }}</td>
            <td>{{ subscription.name || '-' }}</td>
            <td>
              <v-chip
                small
                :color="getRenewalColor(subscription.next_renewal_date)"
                :class="`v-chip-light-bg ${getRenewalColor(subscription.next_renewal_date)}--text font-weight-semibold ${isRenewalClickable(subscription.next_renewal_date) ? 'cursor-pointer' : ''}`"
                @click="isRenewalClickable(subscription.next_renewal_date) && renewSubscription(subscription)"
              >
                {{ formatDate(subscription.next_renewal_date) }}
              </v-chip>
            </td>
            <td class="text-right">
              {{ formatCurrency(subscription.price) }}
            </td>
            <td class="text-center">
              <v-icon
                small
                class=""
                @click="viewRenewals(subscription)"
              >
                {{ icons.mdiHistory }}
              </v-icon>
              &nbsp;
              <v-icon
                small
                class=""
                @click="editSubscription(subscription)"
              >
                {{ icons.mdiPencilOutline }}
              </v-icon>
              &nbsp;
              <v-icon
                small
                class=""
                @click="deleteSubscription(subscriptionKey)"
              >
                {{ icons.mdiTrashCan }}
              </v-icon>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>

    <v-dialog
      v-model="isAddNewSubscriptionModalActive"
      max-width="600px"
    >
      <subscription-form
        :data="subscriptionData"
        :subscription-types="subscriptionTypes"
        @close-dialog="isAddNewSubscriptionModalActive = false"
        @refresh-list="fetchSubscriptions"
      ></subscription-form>
    </v-dialog>

    <v-dialog
      v-model="isAddNewSubscriptionTypeModalActive"
      max-width="500px"
    >
      <subscription-type-form
        @close-dialog="isAddNewSubscriptionTypeModalActive = false"
        @refresh-types="fetchSubscriptionTypes"
      ></subscription-type-form>
    </v-dialog>

    <v-dialog
      v-model="isRenewalLogsDialogActive"
      max-width="600px"
    >
      <v-card>
        <v-card-title>
          View renewals
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="isRenewalLogsDialogActive = false"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-progress-linear
            v-if="renewalLogsLoading"
            indeterminate
            color="primary"
          />
          <template v-else>
            <v-simple-table v-if="renewalLogs.length">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-uppercase">
                      Date
                    </th>
                    <th class="text-uppercase">
                      Description
                    </th>
                    <th class="text-right text-uppercase">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(log, logIndex) in renewalLogs"
                    :key="logIndex"
                  >
                    <td>{{ formatDate(log.created_at) }}</td>
                    <td>{{ log.name || log.note || '-' }}</td>
                    <td class="text-right">
                      {{ formatCurrency(log.price || log.amount || log.total || 0) }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <div
              v-else
              class="text-center"
            >
              No renewals found for this subscription.
            </div>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>

    <confirm ref="confirm"></confirm>
  </v-card>
</template>

<script>
import Confirm from '@/components/Confirm.vue'
import store from '@/store'
import { formatCurrency } from '@core/utils/filter'
import {
  mdiClose,
  mdiHistory,
  mdiPencilOutline,
  mdiPlaylistPlus,
  mdiPlus,
  mdiTrashCan
} from '@mdi/js'
import { onUnmounted, ref } from '@vue/composition-api'
import invoiceStoreModule from '../invoiceStoreModule'
import SubscriptionForm from './SubscriptionForm.vue'
import SubscriptionTypeForm from './SubscriptionTypeForm.vue'

export default {
  components: {
    SubscriptionForm,
    SubscriptionTypeForm,
    Confirm
  },
  setup() {

    const INVOICE_APP_STORE_MODULE_NAME = 'app-invoice'

    // Register module
    if (!store.hasModule(INVOICE_APP_STORE_MODULE_NAME)) {
      store.registerModule(INVOICE_APP_STORE_MODULE_NAME, invoiceStoreModule)
    }

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(INVOICE_APP_STORE_MODULE_NAME)) store.unregisterModule(INVOICE_APP_STORE_MODULE_NAME)
    })

    const isAddNewSubscriptionModalActive = ref(false)
    const isAddNewSubscriptionTypeModalActive = ref(false)
    const isRenewalLogsDialogActive = ref(false)

    const subscriptions = ref([])
    const subscriptionTypes = ref([])
    const renewalLogs = ref([])
    const renewalLogsLoading = ref(false)

    const subscriptionData = ref({})

    const confirm = ref(null)

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString()
    }

    const getRenewalColor = (dateString) => {
      if (!dateString) return 'secondary'
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const renewalDate = new Date(dateString)
      renewalDate.setHours(0, 0, 0, 0)

      // Overdue - red
      if (renewalDate < today) {
        return 'error'
      }

      // Less than a month away - orange/warning
      const oneMonthFromNow = new Date(today)
      oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1)
      if (renewalDate < oneMonthFromNow) {
        return 'warning'
      }

      // More than a month away - green/success
      return 'success'
    }

    const isRenewalClickable = (dateString) => {
      const color = getRenewalColor(dateString)
      return color === 'error' || color === 'warning'
    }

    const renewSubscription = (subscription) => {
      confirm.value.open('Renew subscription', 'This will renew subscription. Continue?').then((confirmResult) => {
        if (confirmResult) {
          store.dispatch('app-invoice/renewSubscription', subscription).then(response => {
            store.dispatch("addNotification", { message: "Subscription was renewed.", type: "success" })
            fetchSubscriptions()
          }).catch(error => {
            console.log(error)
          })
        }
      })
    }

    const viewRenewals = (subscription) => {
      if (!subscription || !subscription.id) return

      isRenewalLogsDialogActive.value = true
      renewalLogsLoading.value = true
      renewalLogs.value = []

      store.dispatch('app-invoice/fetchSubscriptionLogs', subscription.id)
        .then(response => {
          const data = response && response.data ? response.data : []

          if (Array.isArray(data)) {
            renewalLogs.value = data
          } else if (Array.isArray(data.data)) {
            renewalLogs.value = data.data
          } else if (Array.isArray(data.logs)) {
            renewalLogs.value = data.logs
          } else {
            renewalLogs.value = data ? [data] : []
          }
        })
        .catch(error => {
          console.log(error)
          store.dispatch('addNotification', { message: 'Failed to load renewals.', type: 'error' })
        })
        .finally(() => {
          renewalLogsLoading.value = false
        })
    }

    const fetchSubscriptions = () => {
      store.dispatch('app-invoice/fetchSubscriptions', {
      }).then(response => {
        subscriptions.value = response.data
      }).catch(error => {
        console.log(error)
      })
    }

    const fetchSubscriptionTypes = () => {
      store.dispatch('app-invoice/fetchSubscriptionTypes', {
      }).then(response => {
        subscriptionTypes.value = response.data
      }).catch(error => {
        console.log(error)
      })
    }

    fetchSubscriptions()
    fetchSubscriptionTypes()

    const editSubscription = (subscription) => {
      subscriptionData.value = { ...subscription }
      isAddNewSubscriptionModalActive.value = true
    }

    const deleteSubscription = (key) => {
      confirm.value.open('Delete confirmation', 'Are you sure you want to delete this subscription?').then((confirmResult) => {
        if (confirmResult) {
          store.dispatch('app-invoice/deleteSubscription', subscriptions.value[key].id).then(response => {
            subscriptions.value.splice(key, 1)
            store.dispatch("addNotification", { message: "Subscription was removed.", type: "success" })
          }).catch(error => {
            console.log(error)
          })
        }
      })
    }

    const addSubscription = () => {
      subscriptionData.value = {
        customer_id: 0,
        subscription_type_id: null,
        name: '',
        next_renewal_date: '',
        price: 0
      }
      isAddNewSubscriptionModalActive.value = true
    }

    const openSubscriptionTypeModal = () => {
      isAddNewSubscriptionTypeModalActive.value = true
    }

    return {
      subscriptions,
      subscriptionTypes,
      subscriptionData,
      isAddNewSubscriptionModalActive,
      isAddNewSubscriptionTypeModalActive,
      fetchSubscriptions,
      fetchSubscriptionTypes,
      editSubscription,
      addSubscription,
      deleteSubscription,
      confirm,
      formatCurrency,
      formatDate,
      getRenewalColor,
      isRenewalClickable,
      renewSubscription,
      openSubscriptionTypeModal,
      isRenewalLogsDialogActive,
      renewalLogs,
      renewalLogsLoading,
      viewRenewals,
      // icons
      icons: {
        mdiClose,
        mdiHistory,
        mdiPlus,
        mdiPencilOutline,
        mdiTrashCan,
        mdiPlaylistPlus
      },
    }
  }
}
</script>

<style lang="css" scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>

